import i18next from 'i18next';
import i18n from 'i18next';
import jsPDF from 'jspdf';

import { ISummaryStory, TicketType } from 'store/ticket-service/types';

import { normalizeObjectKeys } from 'utils/normalizeObjectKeys';
import { orderDiagrams } from 'utils/orderDiagrams';

import { GeneralStepConversationTopics, NamespacesList, TicketStages } from 'core/enums/common';
import { errorNotify } from 'core/helpers/toasts';

import { ITestCase, ITestPlan } from 'components/TicketSteps/TestCasesStep/types';

const pdfTitleSize = 24;
const pdfSubtitleSize = 16;
const pdfFontSize = 12;

const lineHeightMultiplier = 0.55;
const lineSeparator = pdfFontSize * lineHeightMultiplier;

const millimeterToPixelRatio = 3.78;

const pdfPageMargin = 10;
const pdfPageWidth = 210;
const pdfPageHeight = 297;

const translate = (translaionPath: string) => {
	return i18next.t(translaionPath);
};

const reverseString = (str: string) => {
	return str.split('').reverse().join('');
};

const fetchArslanWessamAFont = async (): Promise<Blob | null> => {
	try {
		const response = await fetch(
			'https://ellogytemplatestorge.blob.core.windows.net/fonts/Arslan-Wessam.ttf'
		);

		return response.blob();
	} catch (e) {
		errorNotify(
			i18n.t('common:errors.arabicFontLoadTitle'),
			i18n.t('common:errors.arabicFontLoadDescription')
		);

		return null;
	}
};

const blobToBase64 = (blob: Blob): Promise<string> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onloadend = () => {
			const base64data = reader.result as string;
			resolve(base64data.split(',')[1]);
		};
		reader.onerror = () => reject(reader.error);
		reader.readAsDataURL(blob);
	});
};

const stringHasOnlyNumbers = (string: string) => {
	// If string contains only numbers, returns false
	const regex = new RegExp(/^\d+$/);

	return regex.test(string);
};

const cleanScenariosNumbering = (array: Array<string>) =>
	array.filter((value) => value && value.length > 1 && !stringHasOnlyNumbers(value));

export const GeneratePdfFromTicket = async (ticketData: TicketType, ticketStep: TicketStages) => {
	const storage = localStorage.getItem('i18nextLng');

	const pdf = new jsPDF();

	let currentPdfY = pdfPageMargin;

	const contentDirection = storage && storage === 'ar' ? 'rtl' : 'ltr';
	const pdfFontFamily = storage === 'ar' ? 'arslamWessamA' : 'lato';

	if (storage === 'ar') {
		const arslanWessamAFont = await fetchArslanWessamAFont();

		if (arslanWessamAFont) {
			const parseFont = await blobToBase64(arslanWessamAFont);

			pdf.addFileToVFS('Arslan Wessam A-normal.ttf', parseFont);
			pdf.addFont('Arslan Wessam A-normal.ttf', 'arslamWessamA', 'normal');

			pdf.addFileToVFS('Arslan Wessam A-bold.ttf', parseFont);
			pdf.addFont('Arslan Wessam A-bold.ttf', 'arslamWessamA', 'bold');
		}
	}

	const checkPdfPageEnd = (fontSize = pdfFontSize) => {
		if (currentPdfY + fontSize * lineHeightMultiplier > pdfPageHeight - pdfPageMargin) {
			pdf.addPage([pdfPageWidth, pdfPageHeight]);
			currentPdfY = pdfPageMargin;
		}
	};

	const splitTextToWidth = (text: string): Array<string> => {
		const words = text.split(' ');
		let currentLine = '';
		const lines = [];

		words.forEach((word) => {
			const currentWidth = pdf.getTextWidth(currentLine + ' ' + word);

			if (currentWidth <= pdfPageWidth - pdfPageMargin * 2) {
				currentLine += currentLine === '' ? word : ` ${word}`;
			} else {
				lines.push(currentLine);
				currentLine = word;
			}
		});

		if (currentLine !== '') lines.push(currentLine);

		return lines;
	};

	const splitTextToWidthRtl = (text: string, fontSize: number, textColor?: [number, number, number]) => {
		const maxWidth = pdfPageWidth - 2 * pdfPageMargin;

		const lines = pdf.splitTextToSize(text, maxWidth);

		lines.forEach((line: string) => {
			checkPdfPageEnd(fontSize);

			const reversedLine = reverseString(line);

			const textWidth =
				(pdf.getStringUnitWidth(reversedLine) * fontSize) / pdf.internal.scaleFactor;

			const lineX = pdfPageMargin + maxWidth - textWidth;

			// Définir la couleur si fournie
			if (textColor) {
				pdf.setTextColor(textColor[0], textColor[1], textColor[2]);
			}

			pdf.text(reversedLine, lineX, currentPdfY);

			// Réinitialiser la couleur à noir si elle était définie
			if (textColor) {
				pdf.setTextColor(0, 0, 0);
			}

			currentPdfY += lineSeparator;
		});
	};

	const pdfAddLine = (line: string, fontSize = pdfFontSize, textColor?: [number, number, number]) => {
		if (contentDirection === 'rtl') {
			splitTextToWidthRtl(line, fontSize, textColor);
		} else {
			splitTextToWidth(line).forEach((subLine: string) => {
				checkPdfPageEnd(fontSize);

				// Définir la couleur si fournie
				if (textColor) {
					pdf.setTextColor(textColor[0], textColor[1], textColor[2]);
				}

				pdf.text(subLine, pdfPageMargin, currentPdfY);

				// Réinitialiser la couleur à noir si elle était définie
				if (textColor) {
					pdf.setTextColor(0, 0, 0);
				}

				currentPdfY += lineSeparator;
			});
		}
	};

	const titleToPdf = (title: string, fontSize = pdfSubtitleSize, textColor?: [number, number, number], centered = false) => {
		checkPdfPageEnd(fontSize);

		pdf.setFontSize(fontSize);
		pdf.setFont(pdfFontFamily, 'normal', 700);

		if (centered) {
			// Centrer le titre - utiliser splitTextToSize pour gérer le retour à la ligne
			const maxWidth = pdfPageWidth - 2 * pdfPageMargin;
			const lines = pdf.splitTextToSize(title, maxWidth);
			
			lines.forEach((line: string) => {
				checkPdfPageEnd(fontSize);

				// Calculer la largeur du texte avec la taille de police actuelle
				const textWidth = pdf.getTextWidth(line);
				// Centrer horizontalement
				const x = (pdfPageWidth - textWidth) / 2;

				// Définir la couleur si fournie
				if (textColor) {
					pdf.setTextColor(textColor[0], textColor[1], textColor[2]);
				}

				pdf.text(line, x, currentPdfY);

				// Réinitialiser la couleur à noir si elle était définie
				if (textColor) {
					pdf.setTextColor(0, 0, 0);
				}

				currentPdfY += fontSize * lineHeightMultiplier;
			});
		} else {
			// Passer la couleur à pdfAddLine pour qu'elle soit appliquée lors de l'affichage
			pdfAddLine(title, fontSize, textColor);
		}

		pdf.setFont(pdfFontFamily, 'normal', 400);
		pdf.setFontSize(pdfFontSize);

		currentPdfY += fontSize * lineHeightMultiplier * 0.5;
	};

	const descriptionToPdf = () => {
		titleToPdf(translate(`${NamespacesList['reportStep']}:titles.description`), pdfSubtitleSize, [0, 102, 204]);

		checkPdfPageEnd();

		pdfAddLine(ticketData.description);

		currentPdfY += 4;
	};

	const userStoriesToPdf = () => {
		titleToPdf(translate(`${NamespacesList['reportStep']}:titles.userStories`), pdfSubtitleSize, [0, 102, 204]);

		// Grouper les user stories par subStage
		const storiesBySubStage = new Map<number, Array<ISummaryStory>>();
		
		ticketData.ticketSummaries?.forEach((story) => {
			// Les stories sans subStage sont considérées comme "Functional requirements" (0)
			const subStage = story.subStage ?? GeneralStepConversationTopics['Functional requirements'];
			
			if (!storiesBySubStage.has(subStage)) {
				storiesBySubStage.set(subStage, []);
			}
			storiesBySubStage.get(subStage)!.push(story);
		});

		// Trier les subStages par ordre numérique
		const sortedSubStages = Array.from(storiesBySubStage.keys()).sort((a, b) => a - b);

		let globalStoryIndex = 0;
		let previousSubStage: number | null = null;

		sortedSubStages.forEach((subStage) => {
			const stories = storiesBySubStage.get(subStage)!;
			const isNewSubStage = previousSubStage !== null && previousSubStage !== subStage;
			
			// Afficher le titre du subStage
			// Pour subStage 0 (Functional requirements), on n'affiche pas de titre supplémentaire car c'est déjà le titre principal
			// Pour les autres subStages (1=Performance, 2=Scalability, etc.), on affiche le titre
			if (subStage !== GeneralStepConversationTopics['Functional requirements']) {
				currentPdfY += 4;
				checkPdfPageEnd();
				
				// Récupérer le nom du subStage depuis l'enum (ex: subStage 1 -> 'Performance')
				const subStageName = GeneralStepConversationTopics[subStage] as keyof typeof GeneralStepConversationTopics;
				// Afficher le titre du subStage en bleu (RGB: 0, 102, 204)
				// Traduire le nom du subStage et ajouter les deux-points après
				const translatedSubStageName = translate(`${NamespacesList['generalStep']}:substages.${subStageName}`);
				titleToPdf(
					`${translatedSubStageName} :`,
					pdfSubtitleSize,
					[0, 102, 204] // Bleu (#0066CC)
				);
			}

			stories.forEach((story, storyIndexInSubStage) => {
				// Ajouter une ligne de séparation avant chaque User Story
				// Sauf la toute première user story (globalStoryIndex === 0)
				// Et sauf la première user story d'un nouveau subStage (quand on vient d'afficher un titre de subStage)
				const shouldAddSeparator = globalStoryIndex > 0 && !(isNewSubStage && storyIndexInSubStage === 0);
				
				if (shouldAddSeparator) {
					currentPdfY += 4;
					checkPdfPageEnd();
					
					// Dessiner une ligne horizontale centrée
					const lineWidth = pdfPageWidth - 2 * pdfPageMargin;
					const lineX = pdfPageMargin;
					const lineY = currentPdfY;
					pdf.setDrawColor(110, 110, 110); // Couleur grise #6e6e6e pour la ligne de séparation
					pdf.line(lineX, lineY, lineX + lineWidth, lineY);
					
					currentPdfY += 4;
				}

				pdfAddLine(`${globalStoryIndex + 1}. ${story.data}`);

				currentPdfY += 2;

				// Afficher le titre "User Scenario:" uniquement s'il y a des scenarios
				if (story?.summaryScenarios?.length) {
					titleToPdf(translate(`${NamespacesList['generalStep']}:commonText.scenarios`), pdfFontSize, [0, 102, 204]);

					story.summaryScenarios.forEach((scenario, index) => {
						pdfAddLine(`${index + 1}. ${scenario.title}`);

						pdfAddLine(scenario.description);

						currentPdfY += 2;
					});

					currentPdfY += 2;
				}

				// Afficher le titre "Acceptance Criteria:" uniquement s'il y a des critères
				if (story?.summaryAcceptanceCriteria?.length) {
					titleToPdf(
						translate(`${NamespacesList['generalStep']}:commonText.acceptanceCriteria`),
						pdfFontSize,
						[0, 102, 204]
					);

					story.summaryAcceptanceCriteria.forEach((acceptanceCriteria, index) => {
						pdfAddLine(`${index + 1}. ${acceptanceCriteria.title}`);

						pdfAddLine(acceptanceCriteria.description);

						currentPdfY += 1;
					});
				}

				globalStoryIndex++;
			});
			
			previousSubStage = subStage;
		});

		currentPdfY += 4;
	};

	const parseTableValue = (
		value: string | Array<string> | Record<string, any>,
		index = 0
	): string | Array<string> | Record<string, any> => {
		if (typeof value === 'string') return [`${index ? `${index}.${value}` : value}`];

		if (Array.isArray(value))
			return value.map((deepValue, index) => parseTableValue(deepValue, index + 1));

		if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
			return Object.entries(value).map(([key, deepValue]) => [
				parseTableValue(
					`${translate(`${NamespacesList['useCasesStep']}:descriptionTable.${key}`)}:`
				),
				parseTableValue(deepValue),
			]);
		}

		return value;
	};

	const parseTableToPdf = (tableObj: Record<string, any>) => {
		return Object.entries(tableObj).map(([key, value]) => [
			key,
			Array(parseTableValue(value)).flat(6),
		]);
	};

	const addPdfImage = async (imageUrl: string): Promise<Record<string, any> | null> => {
		const response = await fetch(imageUrl);

		if (response.ok) {
			const imageBlob = await response.blob();
			const img = new Image();

			return new Promise((resolve) => {
				img.onload = () => {
					const width =
						img.width / millimeterToPixelRatio > pdfPageWidth - 2 * pdfPageMargin
							? pdfPageWidth - 2 * pdfPageMargin
							: img.width / millimeterToPixelRatio;
					const height =
						img.height / millimeterToPixelRatio > pdfPageHeight - currentPdfY - 2 * pdfPageMargin
							? pdfPageHeight - currentPdfY - 2 * pdfPageMargin
							: img.height / millimeterToPixelRatio;
					const marginLeft =
						width >= pdfPageWidth - 2 * pdfPageMargin
							? pdfPageMargin
							: (pdfPageWidth - 2 * pdfPageMargin - width) / 2;

					resolve({
						img,
						format: 'PNG',
						marginX: marginLeft,
						marginY: currentPdfY,
						width,
						height,
						blob: imageBlob,
					});
				};

				img.src = URL.createObjectURL(imageBlob);
			});
		} else {
			return new Promise((resolve) => resolve(null));
		}
	};

	const useCasesToPdf = async () => {
		titleToPdf(translate(`${NamespacesList['reportStep']}:titles.useCases`), pdfSubtitleSize, [0, 102, 204]);

		if (ticketData?.useCases) {
			for (const [index, usecase] of ticketData.useCases.entries()) {
				titleToPdf(
					`${translate(`${NamespacesList['useCasesStep']}:generalText.useCase`)} #${index + 1}: ${
						usecase.title
					}`,
					pdfSubtitleSize,
					[0, 102, 204]
				);

				const parsedTable = parseTableToPdf(JSON.parse(usecase.tables[0].table));

				parsedTable.forEach((row) => {
					if (typeof row[0] === 'string')
						titleToPdf(
							translate(`${NamespacesList['useCasesStep']}:descriptionTable.${row[0]}`),
							pdfFontSize,
							[0, 102, 204]
						);

					if (Array.isArray(row[1]))
						row[1].forEach((point) => {
							if (typeof point === 'string') {
								const fontWeight = point === 'Role:' || point === 'Responsibilities:' ? 700 : 400;
								if (point.endsWith(':')) currentPdfY += 2;
								pdf.setFont(pdfFontFamily, 'normal', fontWeight);
								pdfAddLine(point);
							}
						});

					currentPdfY += 2;
				});

				pdf.addPage([pdfPageWidth, pdfPageHeight]);
				currentPdfY = pdfPageMargin;

				if (orderDiagrams(usecase.diagrams).filter((diagram) => diagram.pictureLink).length) {
					for (const diagram of orderDiagrams(usecase.diagrams).filter(
						(diagram) => diagram.pictureLink
					)) {
						titleToPdf(
							translate(`${NamespacesList['useCasesStep']}:diagrams.${diagram.title}`),
							pdfFontSize,
							[0, 102, 204]
						);

						const imageData = await addPdfImage(diagram.pictureLink.replace('svg', 'png'));

						if (imageData !== null) {
							const { img, type, marginX, marginY, width, height, blob } = imageData;

							pdf.addImage(img, type, marginX, marginY, width, height);

							URL.revokeObjectURL(URL.createObjectURL(blob));

							pdf.addPage([pdfPageWidth, pdfPageHeight]);
							currentPdfY = pdfPageMargin;
						}
					}
				}
			}
		}
	};

	const testPlanToPdf = (testPlan: ITestPlan) => {
		Object.entries(testPlan ?? {}).forEach(([key, value]) => {
			const parsedValue = Array.isArray(value)
				? value
				: typeof value === 'string'
					? value.split('\n')
					: [];

			if (key !== 'id' && parsedValue?.length) {
				if (Array.isArray(parsedValue)) {
					titleToPdf(
						`${translate(`${NamespacesList['testCasesStep']}:testPlan.${key}`)}: `,
						pdfFontSize,
						[0, 102, 204]
					);
					parsedValue.forEach((item, index) => {
						pdfAddLine(`${index + 1}.${item}`);
					});
				} else {
					titleToPdf(
						`${translate(`${NamespacesList['testCasesStep']}:testPlan.${key}`)}: `,
						pdfFontSize,
						[0, 102, 204]
					);
					pdfAddLine(parsedValue);
				}
			}
		});

		currentPdfY += 3;
	};

	const testScenarioToPdf = (testScenarios: Array<string>) => {
		titleToPdf(translate(`${NamespacesList['testCasesStep']}:testScenarios.title`), pdfFontSize, [0, 102, 204]);

		testScenarios.forEach((scenario, index) => {
			pdfAddLine(`${index + 1}.${scenario}`);
		});
	};

	const testCasesToPdf = (testCases: Array<ITestCase>) => {
		titleToPdf(translate(`${NamespacesList['testCasesStep']}:testCases.title`), pdfFontSize, [0, 102, 204]);

		(testCases ?? []).forEach((testCase: ITestCase) => {
			Object.entries(testCase).forEach(([key, value]) => {
				const parsedValue = Array.isArray(value)
					? value
					: typeof value === 'string'
						? value.split('\n')
						: [];

				if (key !== 'id') {
					if (Array.isArray(parsedValue)) {
						titleToPdf(
							`${translate(`${NamespacesList['testCasesStep']}:testCases.${key}`)}: `,
							pdfFontSize,
							[0, 102, 204]
						);
						parsedValue.forEach((item, index) => {
							pdfAddLine(`${index + 1}.${item}`);
						});
					} else {
						titleToPdf(
							`${translate(`${NamespacesList['testCasesStep']}:testCases.${key}`)}: `,
							pdfFontSize,
							[0, 102, 204]
						);
						pdfAddLine(parsedValue);
					}

					currentPdfY += 2;
				}
			});

			currentPdfY += 2;
		});
	};

	const testsToPdf = () => {
		titleToPdf(translate(`${NamespacesList['reportStep']}:titles.testCases`), pdfSubtitleSize, [0, 102, 204]);

		const cleanedTests = ticketData.userStoryTests
			? ticketData.userStoryTests.map((test) => ({
					...test,
					testScenarios: cleanScenariosNumbering(test.testScenarios),
					useCaseTitle: normalizeObjectKeys(test)['usecasetitle'],
			  }))
			: [];

	cleanedTests.forEach((userStoryTest, index) => {
		titleToPdf(
			`${translate(`${NamespacesList['testCasesStep']}:testPlan.title`)}: ${index + 1}.${normalizeObjectKeys(userStoryTest)['usecasetitle']}`,
			pdfFontSize,
			[0, 102, 204]
		);

			currentPdfY += 2;

			testPlanToPdf(userStoryTest.testPlan);

			currentPdfY += 3;

			testScenarioToPdf(userStoryTest.testScenarios ?? []);

			currentPdfY += 3;

			testCasesToPdf(userStoryTest.testCases ?? []);
		});

		currentPdfY += 4;
	};

	const NotificationsToPdf = () => {
		titleToPdf(translate(`${NamespacesList['reportStep']}:titles.notifications`), pdfSubtitleSize, [0, 102, 204]);

		ticketData.notifications.forEach((notification) => {
			pdfAddLine(notification.title);

			pdfAddLine(notification.description);

			pdfAddLine(
				`${translate(`${NamespacesList['notificationsStep']}:notifications.Email`)} - ${String(
					notification.email
				)}`
			);
			pdfAddLine(
				`${translate(`${NamespacesList['notificationsStep']}:notifications.Push`)} - ${String(
					notification.push
				)}`
			);
			pdfAddLine(
				`${translate(`${NamespacesList['notificationsStep']}:notifications.SMS`)} - ${String(
					notification.sms
				)}`
			);

			currentPdfY += 4;
		});
	};

	const businessImpactToPdf = () => {
		if (ticketData?.businessImpact?.length) {
			titleToPdf(
				translate(`${NamespacesList['reportStep']}:titles.businessImpact`),
				pdfSubtitleSize,
				[0, 102, 204]
			);

			const businessImpact: Array<string> = JSON.parse(ticketData.businessImpact);

			businessImpact.forEach((impactItem) => {
				pdfAddLine(impactItem);
			});

			currentPdfY += 4;
		}
	};

	pdf.setFont(pdfFontFamily, 'normal', 400);
	pdf.setFontSize(pdfFontSize);

	// Afficher le titre principal centré
	titleToPdf(ticketData.title, pdfTitleSize, [0, 102, 204], true);

	if (ticketStep === TicketStages.general || ticketStep === TicketStages.report) {
		descriptionToPdf();
		businessImpactToPdf();
		userStoriesToPdf();
	}

	if (ticketStep === TicketStages.useCases || ticketStep === TicketStages.report)
		await useCasesToPdf();

	if (ticketStep === TicketStages.testCases || ticketStep === TicketStages.report) testsToPdf();

	if (ticketStep === TicketStages.notifications || ticketStep === TicketStages.report)
		NotificationsToPdf();

	return pdf;
};
